/**
 * the steps this step is linked to (services/recipe/step_link.py).
 *
 * "Instead of a dependency, one more option — link from the previous step to take the value from it; even linked, both
 * steps run in parallel."
 *
 * A link says this step works on the linked step's output and opens TOGETHER with it, not after it — the two run side by
 * side on the tablet. A dependency this step states (Depends on) still decides when it opens. Only steps BEFORE it at the
 * same level are offered, as for a dependency: the server refuses anything else.
 */
import React, { useMemo } from "react";
import { Select, Typography } from "antd";
import { dependencyCandidates } from "./DependsOnField";

const { Text } = Typography;

export default function LinkedToField({ step, operation, readOnly, onChange }) {
  const candidates = useMemo(() => dependencyCandidates(operation, step?.code), [operation, step?.code]);
  const value = (step?.linked_steps || []).filter(Boolean);
  const deps = (step?.after_steps || []).filter(Boolean);
  const label = (c) => `${c.code} · ${c.title || c.label || ""}`.trim();
  return (
    <div>
      <Select
        mode="multiple" size="small" style={{ width: "100%" }} allowClear disabled={readOnly || !candidates.length}
        value={value} placeholder={candidates.length ? "Not linked" : "No step before this one at its level"}
        onChange={(v) => onChange({ linked_steps: v || [] })}
        optionFilterProp="label"
        options={candidates.map((c) => ({ value: c.code, label: label(c) }))}
      />
      <Text className="u0-edit-hint" type="secondary" style={{ display: "block", marginTop: 6, fontSize: "calc(12.5px * var(--dz-fs, 1))" }}>
        {!candidates.length
          ? "This is the first step here (of the operation, or of its repeat / lane), so there is no earlier step to link to."
          : !value.length
          ? "Not linked: this step opens as Depends on says."
          : deps.length
          ? `Works on the output of ${value.join(", ")}. Depends on is set, so it still opens once ${deps.join(", ")} ${deps.length === 1 ? "is" : "are"} done.`
          : `Works on the output of ${value.join(", ")} and opens together with ${value.length === 1 ? "it" : "them"} — on the tablet they run side by side.`}
      </Text>
    </div>
  );
}
